import { AlternativeDesignsService } from "@/common/api.service";
import { PlanProvisionsService } from "@/common/api.service";

import {
  ALTERNATIVE_DESIGNS_FETCH,
  ALTERNATIVE_DESIGN_UPDATE,
  ALTERNATIVE_DESIGNS_CALCULATE_SAVING,
  ALTERNATIVE_DESIGN_ADD_NEW,
  ALTERNATIVE_DESIGN_ARCHIVE,
  ALTERNATIVE_DESIGN_UNARCHIVE,
  ALTERNATIVE_DESIGN_REMOVE,
  ALTERNATIVE_DESIGNS_REMOVE_ARCHIVED
} from "./actions.type";
import {
  ALTERNATIVE_DESIGNS_SET,
  ALTERNATIVE_DESIGNS_UPDATED,
  ALTERNATIVE_DESIGNS_INDUSTRY_BENCHMARK_SET,
  ALTERNATIVE_DESIGNS_SURVEY_SET,
  ALTERNATIVE_DESIGN_ADD_CURRENT,
  ALTERNATIVE_DESIGN_ADD_ARCHIVED,
  ALTERNATIVE_DESIGN_DELETE_CURRENT,
  ALTERNATIVE_DESIGN_DELETE_ARCHIVED,
  ALTERNATIVE_DESIGNS_DELETE_ARCHIVED,
  ALTERNATIVE_DESIGNS_CALCULATED,
  ALTERNATIVE_DESIGNS_RESET_CALCULATED,
  ALTERNATIVE_DESIGNS_HIGHLIGHT_DIFFERENCES_CHANGE,
  ALTERNATIVE_DESIGNS_SET_LAG_INDICATORS_SELECTED_YEAR,
  STEP_SET_ALTERNATIVE_DESIGNS_STATE,
  FLASH_MESSAGE_SET_WARNING_MESSAGE
} from "./mutations.type";

const state = {
  originalDesign: null,
  currentDesigns: [],
  archivedDesigns: [],
  industryBenchmark: {},
  survey: {},
  highlightDifferences: false,
  lagIndicatorsSelectedYear: null, // TODO: Remove
  lagIndicatorYears: [],
  calculated: false,
  calculating: false
};

const getters = {
  originalDesign(state) {
    return state.originalDesign;
  },
  designColors() {
    return ["#5dade2", "#7caba2", "#f3b95a", "#667d95"];
  },
  designColorClasses() {
    return ["primary", "secondary", "yellow", "dark"];
  },
  archivedDesigns(state) {
    return state.archivedDesigns;
  },
  industryBenchmark(state) {
    return state.industryBenchmark;
  },
  survey(state) {
    return state.survey;
  },
  currentDesigns(state) {
    return state.currentDesigns;
  },
  designs(state) {
    return [state.originalDesign].concat(state.currentDesigns);
  },
  highlightDifferences(state) {
    return state.highlightDifferences;
  },
  lagIndicatorsSelectedYear(state) {
    return state.lagIndicatorsSelectedYear;
  },
  lagIndicatorYears(state) {
    return state.lagIndicatorYears;
  },
  calculated(state) {
    return state.calculated;
  },
  calculating(state) {
    return state.calculating;
  }
};
const actions = {
  async [ALTERNATIVE_DESIGNS_FETCH]({ commit }, reportId) {
    commit(ALTERNATIVE_DESIGNS_RESET_CALCULATED);
    return AlternativeDesignsService.get(reportId)
      .then(({ data }) => {
        commit(ALTERNATIVE_DESIGNS_SET, data.plan_provisions);
        commit(ALTERNATIVE_DESIGNS_SURVEY_SET, data.survey);
        commit(
          ALTERNATIVE_DESIGNS_INDUSTRY_BENCHMARK_SET,
          data.industry_benchmark
        );
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [ALTERNATIVE_DESIGN_UPDATE]({ commit }, { design }) {
    commit(ALTERNATIVE_DESIGNS_RESET_CALCULATED);
    commit(STEP_SET_ALTERNATIVE_DESIGNS_STATE, false);
    commit(ALTERNATIVE_DESIGNS_UPDATED, design); // positive updating
    return PlanProvisionsService.update(design);
  },
  async [ALTERNATIVE_DESIGN_ADD_NEW]({ state, commit }) {
    if (state.currentDesigns.length > 2) return;
    commit(ALTERNATIVE_DESIGNS_RESET_CALCULATED);
    commit(STEP_SET_ALTERNATIVE_DESIGNS_STATE, false);
    return AlternativeDesignsService.copy(
      state.originalDesign.report_id,
      state.originalDesign.id
    )
      .then(({ data }) => {
        commit(ALTERNATIVE_DESIGN_ADD_CURRENT, data);
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [ALTERNATIVE_DESIGN_ARCHIVE]({ commit }, design) {
    commit(ALTERNATIVE_DESIGNS_RESET_CALCULATED);
    commit(STEP_SET_ALTERNATIVE_DESIGNS_STATE, false);
    commit(ALTERNATIVE_DESIGN_ADD_ARCHIVED, design);
    commit(ALTERNATIVE_DESIGN_DELETE_CURRENT, design.id);
    return AlternativeDesignsService.update(
      state.originalDesign.report_id,
      design.id,
      { plan_type: "archived" }
    );
  },
  async [ALTERNATIVE_DESIGN_UNARCHIVE]({ commit }, design) {
    if (state.currentDesigns.length > 2) return;
    commit(ALTERNATIVE_DESIGNS_RESET_CALCULATED);
    commit(STEP_SET_ALTERNATIVE_DESIGNS_STATE, false);
    commit(ALTERNATIVE_DESIGN_ADD_CURRENT, design);
    commit(ALTERNATIVE_DESIGN_DELETE_ARCHIVED, design.id);
    return AlternativeDesignsService.update(
      state.originalDesign.report_id,
      design.id,
      { plan_type: "current" }
    );
  },
  async [ALTERNATIVE_DESIGN_REMOVE]({ commit }, id) {
    commit(ALTERNATIVE_DESIGNS_RESET_CALCULATED);
    commit(STEP_SET_ALTERNATIVE_DESIGNS_STATE, false);
    commit(ALTERNATIVE_DESIGN_DELETE_CURRENT, id); // positive deleting
    commit(ALTERNATIVE_DESIGN_DELETE_ARCHIVED, id); // positive deleting
    return AlternativeDesignsService.destroy(
      state.originalDesign.report_id,
      id
    );
  },
  async [ALTERNATIVE_DESIGNS_REMOVE_ARCHIVED]({ commit, state }) {
    state.archivedDesigns.forEach(design =>
      AlternativeDesignsService.destroy(
        state.originalDesign.report_id,
        design.id
      )
    );
    commit(ALTERNATIVE_DESIGNS_DELETE_ARCHIVED);
  },
  async [ALTERNATIVE_DESIGNS_CALCULATE_SAVING]({ commit }) {
    commit(ALTERNATIVE_DESIGNS_RESET_CALCULATED);
    commit(STEP_SET_ALTERNATIVE_DESIGNS_STATE, false);
    state.calculating = true;
    return AlternativeDesignsService.calculateSavingBehavior(
      state.originalDesign.report_id
    )
      .then(({ data }) => {
        commit(FLASH_MESSAGE_SET_WARNING_MESSAGE, data.warning_message);
        commit(ALTERNATIVE_DESIGNS_CALCULATED, data.plan_provisions);
        commit(STEP_SET_ALTERNATIVE_DESIGNS_STATE, true);
      })
      .catch(error => {
        state.calculating = false;
        throw new Error(error);
      });
  }
};

const mutations = {
  [ALTERNATIVE_DESIGNS_SET](state, alternativeDesigns) {
    state.originalDesign = alternativeDesigns.find(
      design => design.plan_type === "original"
    );
    state.currentDesigns = alternativeDesigns
      .filter(design => design.plan_type === "current")
      .sort((a, b) => a.serial_number - b.serial_number);
    state.archivedDesigns = alternativeDesigns.filter(
      design => design.plan_type === "archived"
    );
  },
  [ALTERNATIVE_DESIGNS_UPDATED](state, updatedDesign) {
    const design = state.currentDesigns.find(
      design => design.id === updatedDesign.id
    );
    Object.assign(design, updatedDesign);
  },
  [ALTERNATIVE_DESIGNS_INDUSTRY_BENCHMARK_SET](state, industryBenchmark) {
    state.industryBenchmark = industryBenchmark;
  },
  [ALTERNATIVE_DESIGNS_SURVEY_SET](state, survey) {
    state.survey = survey;
  },
  [ALTERNATIVE_DESIGN_ADD_CURRENT](state, design) {
    state.currentDesigns.push(design);
  },
  [ALTERNATIVE_DESIGN_ADD_ARCHIVED](state, design) {
    state.archivedDesigns.push(design);
  },
  [ALTERNATIVE_DESIGN_DELETE_CURRENT](state, designId) {
    state.currentDesigns = state.currentDesigns.filter(
      design => design.id !== designId
    );
  },
  [ALTERNATIVE_DESIGN_DELETE_ARCHIVED](state, designId) {
    state.archivedDesigns = state.archivedDesigns.filter(
      design => design.id !== designId
    );
  },
  [ALTERNATIVE_DESIGNS_DELETE_ARCHIVED](state) {
    state.archivedDesigns = [];
  },
  [ALTERNATIVE_DESIGNS_CALCULATED](state, alternativeDesigns) {
    state.originalDesign = alternativeDesigns.find(
      design => design.plan_type === "original"
    );
    state.currentDesigns[0] &&
      Object.assign(
        state.currentDesigns[0],
        alternativeDesigns.find(
          design => design.id === state.currentDesigns[0].id
        )
      );
    state.currentDesigns[1] &&
      Object.assign(
        state.currentDesigns[1],
        alternativeDesigns.find(
          design => design.id === state.currentDesigns[1].id
        )
      );
    state.currentDesigns[2] &&
      Object.assign(
        state.currentDesigns[2],
        alternativeDesigns.find(
          design => design.id === state.currentDesigns[2].id
        )
      );

    state.lagIndicatorYears = Object.keys(
      state.originalDesign.participation_rate
    );
    state.lagIndicatorsSelectedYear = state.lagIndicatorYears[0];

    state.calculated = true;
    state.calculating = false;
  },
  [ALTERNATIVE_DESIGNS_HIGHLIGHT_DIFFERENCES_CHANGE](state) {
    state.highlightDifferences = !state.highlightDifferences;
  },
  [ALTERNATIVE_DESIGNS_RESET_CALCULATED](state) {
    state.calculated = false;
    state.calculating = false;
  },
  [ALTERNATIVE_DESIGNS_SET_LAG_INDICATORS_SELECTED_YEAR](state, year) {
    state.lagIndicatorsSelectedYear = year;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
